import { useEffect, useRef, useState } from 'react';
import { initialPosterFields, posterFieldGroups, type PosterFieldId } from './fields';
import { savePosterField } from './mockFieldSave';

type Status = 'idle' | 'saving' | 'saved' | 'failed';
const fixed = new Set<PosterFieldId>(posterFieldGroups.flatMap(group => group.fields.filter(field => field.required).map(field => field.id)));
export function usePosterFields() {
  const [values, setValues] = useState(initialPosterFields);
  const [confirmed, setConfirmed] = useState(initialPosterFields);
  const [status, setStatus] = useState<Partial<Record<PosterFieldId, Status>>>({});
  const draft = useRef({ ...initialPosterFields });
  const requests = useRef(new Map<PosterFieldId, AbortController>());
  const timers = useRef(new Map<PosterFieldId, ReturnType<typeof setTimeout>>());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    const pendingRequests = requests.current;
    const feedbackTimers = timers.current;
    return () => {
      mounted.current = false;
      pendingRequests.forEach(request => request.abort());
      pendingRequests.clear();
      feedbackTimers.forEach(clearTimeout);
      feedbackTimers.clear();
    };
  }, []);
  function feedback(id: PosterFieldId, next: Status) {
    setStatus(current => ({ ...current, [id]: next }));
  }
  async function persist(id: PosterFieldId, visible: boolean) {
    if (fixed.has(id)) return;
    requests.current.get(id)?.abort();
    const previousTimer = timers.current.get(id);
    if (previousTimer !== undefined) clearTimeout(previousTimer);
    timers.current.delete(id);
    const request = new AbortController();
    requests.current.set(id, request);
    feedback(id, 'saving');
    try {
      await savePosterField(id, visible, request.signal);
      if (!mounted.current || requests.current.get(id) !== request) return;
      requests.current.delete(id);
      setConfirmed(current => ({ ...current, [id]: visible }));
      feedback(id, 'saved');
      timers.current.set(id, setTimeout(() => {
        if (mounted.current) feedback(id, 'idle');
        timers.current.delete(id);
      }, 2000));
    } catch {
      if (!mounted.current || requests.current.get(id) !== request) return;
      requests.current.delete(id);
      feedback(id, 'failed');
    }
  }
  function change(id: PosterFieldId, visible: boolean) {
    if (fixed.has(id) || draft.current[id] === visible) return;
    draft.current[id] = visible;
    setValues(current => ({ ...current, [id]: visible }));
    void persist(id, visible);
  }
  return { values, confirmed, status, change, retry: (id: PosterFieldId) => persist(id, draft.current[id]) };
}
